/**
 * Medical-Fields landing-page content — XION Medical Philippines.
 *
 * XION does not publish standalone "/medical-fields/*" pages, so this content is
 * ADAPTED (not verbatim) from xion-medical.com: every system / product mapping,
 * application and capability below reflects XION's real catalog (see data/products/*).
 * No hardware specs are fabricated. Systems are referenced by slug and resolved
 * against completeProductsData at render time so names/images/taglines stay in sync.
 */

export const medicalFields = [
  {
    slug: 'ent',
    name: 'ENT',
    fullName: 'Ear, Nose & Throat',
    kicker: '// Otorhinolaryngology & Phoniatrics',
    tagline: 'The complete ENT imaging platform — office to operating theatre',
    heroImage: '/products/transparent/endostrobe-spectar-system-web.png',
    intro: [
      'For more than 30 years XION has specialised in endoscopic imaging for ear, nose and throat (ENT) medicine and phoniatrics. A single, modular platform covers the full ENT workflow — from the consultation room to the operating theatre — combining flexible and rigid endoscopy, laryngo-stroboscopy, voice diagnostics and FEES swallowing examinations.',
      'Native sensor resolutions up to 4K, 2D and 3D visualisation, the world-first 3D stroboscopy and PIET image enhancement give ENT specialists exceptional diagnostic clarity, while the DiVAS software captures, analyses and archives every examination.',
    ],
    applications: [
      'Laryngoscopy & video-stroboscopy',
      'Voice & phoniatric diagnostics (EGG, Voice Range Profile)',
      'FEES — flexible endoscopic evaluation of swallowing',
      'Rhinoscopy & sinuscopy',
      'Otoscopy',
      'Functional endoscopic sinus surgery (FESS)',
      'Office & mobile endoscopy',
    ],
    systems: [
      { slug: 'endostrobe-spectar', blurb: 'The flagship laryngo-stroboscopy & phoniatrics workstation — 4K, 3D and world-first 3D stroboscopy.' },
      { slug: 'endoflex-spectar', blurb: 'Flexible video endoscopy for ENT diagnostics and swallowing examinations.' },
      { slug: 'endocompact-veezar', blurb: 'A compact, all-in-one camera system for everyday ENT endoscopy.' },
      { slug: 'endooffice-spectar', blurb: 'A space-saving office endoscopy solution for the consultation room.' },
    ],
    products: [
      { name: 'Spectar Video Nasopharyngoscopes', link: '/products/flexible-video-endoscopes/spectar-video-nasopharyngoscopes', blurb: 'High-resolution flexible chip-on-tip nasopharyngoscopes (XN series).' },
      { name: 'Flexible Fibre-Optic Endoscopes (EF-N)', link: '/products/flexible-fibre-optic-endoscopes/ef-n', blurb: 'Slim fibre-optic naso-pharyngo-laryngoscopes, incl. paediatric options.' },
      { name: 'Rigid Laryngoscopes', link: '/products/rigid-endoscopes/laryngoscopes', blurb: 'Rigid optics for clear laryngeal and stroboscopic views.' },
      { name: 'Rigid Sinuscopes', link: '/products/rigid-endoscopes/sinuscopes', blurb: 'For diagnostic rhinoscopy and endoscopic sinus surgery.' },
      { name: 'Rigid Otoscopes', link: '/products/rigid-endoscopes/otoscopes', blurb: 'Fine-diameter otoscopes for the ear canal and middle ear.' },
      { name: 'Spectar Camera Head HD with Microphone', link: '/products/camera-heads/spectar-camera-head-hd-microphone', blurb: 'Integrated microphone for synchronised voice diagnostics.' },
      { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8', blurb: 'Documentation, stroboscopy analysis, EGG, FEES and voice modules.' },
    ],
    highlights: [
      { title: 'World-first 3D stroboscopy', desc: 'Three-dimensional visualisation of the entire vocal-fold vibration.' },
      { title: 'PIET image enhancement', desc: 'Lumino, chromo and spectro modes for situational visualisation.' },
      { title: 'Real-time EGG overlay', desc: 'Electroglottography superimposed on the live stroboscopic image.' },
      { title: 'DiVAS documentation', desc: 'Capture, analyse and archive — DICOM / HL7 / GDT ready.' },
    ],
  },
  {
    slug: 'gynecology',
    name: 'Gynaecology',
    fullName: 'Gynaecology',
    kicker: '// Hysteroscopy & Gynaecological Surgery',
    tagline: 'High-definition and true 3D imaging for minimally invasive gynaecology',
    heroImage: '/products/transparent/endosurgery-3d-spectar-web.png',
    intro: [
      'XION\u2019s EndoSURGERY imaging platform brings high-definition and true 3D visualisation to minimally invasive gynaecology. The same processor, light source and camera technology support both hysteroscopy and gynaecological laparoscopy.',
      'True Full 3D depth perception, 4K clarity, rapid 2D/3D switching and PIET image enhancement help surgeons operate with confidence, while DiVAS handles image documentation and archiving.',
    ],
    applications: [
      'Diagnostic & operative hysteroscopy',
      'Gynaecological laparoscopy',
      'Minimally invasive pelvic surgery',
      '3D-assisted dissection & suturing',
      'Image documentation & archiving',
    ],
    systems: [
      { slug: 'endosurgery-spectar', blurb: 'The versatile HD/4K surgical imaging system for endoscopic gynaecology.' },
      { slug: 'endosurgery-3d-spectar', blurb: 'Premium true-3D platform for enhanced depth perception in complex procedures.' },
    ],
    products: [
      { name: 'Rigid Hysteroscopes (2.9 mm 30\u00b0)', link: '/products/rigid-endoscopes/hysteroscopes', blurb: 'Fine-diameter optics for diagnostic and operative hysteroscopy.' },
      { name: 'Rigid Laparoscopes (10 mm 30\u00b0)', link: '/products/rigid-endoscopes/laparoscopes', blurb: 'Robust laparoscopic optics for pelvic and abdominal access.' },
      { name: 'XION 3D Endoscope Attachments', link: '/products/rigid-endoscopes/xion-3d-endoscope', blurb: 'Add true stereoscopic 3D to compatible procedures.' },
      { name: 'MATRIX PL Spectar Camera Processor', link: '/products/camera-processors-for-surgery/matrix-pl-spectar', blurb: 'High-end 4K / 3D surgical camera processor.' },
      { name: 'Spectar 3D Camera Head', link: '/products/camera-heads/spectar-camera-head-3d-hd', blurb: 'Stereoscopic camera head for depth-accurate imaging.' },
      { name: '3D Medical-Grade Monitors', link: '/products/medical-grade-monitors/medical-monitor-32-surgical', blurb: 'Surgical 3D displays for the operating theatre.' },
    ],
    highlights: [
      { title: 'True Full 3D', desc: 'Stereoscopic depth perception for precise instrument handling.' },
      { title: '4K UHD clarity', desc: 'Four times the detail of Full HD imaging.' },
      { title: 'Rapid 2D / 3D switching', desc: 'Switch modes instantly to suit the step at hand.' },
      { title: 'PIET enhancement', desc: 'Situational image-enhancement modes during surgery.' },
    ],
  },
  {
    slug: 'laparoscopy',
    name: 'Laparoscopy',
    fullName: 'Laparoscopy & Minimally Invasive Surgery',
    kicker: '// Minimally Invasive Surgery',
    tagline: 'Brilliant, stable imaging with confident depth perception',
    heroImage: '/products/transparent/endosurgery-spectar-web.png',
    intro: [
      'Minimally invasive surgery demands brilliant, stable imaging and confident depth perception. The EndoSURGERY Spectar and EndoSURGERY 3D Spectar systems combine MATRIX camera processors, high-power MATRIX LED illumination and 4K / 3D camera heads to deliver outstanding visualisation for laparoscopic procedures.',
      'One modular platform serves general, gynaecological and abdominal surgery, with PIET image enhancement and DiVAS documentation built in.',
    ],
    applications: [
      'General laparoscopic surgery',
      '3D laparoscopy',
      'Abdominal & pelvic procedures',
      'Endoscopic visualisation in the OR',
      'Image documentation & archiving',
    ],
    systems: [
      { slug: 'endosurgery-spectar', blurb: 'HD/4K surgical imaging for everyday laparoscopic procedures.' },
      { slug: 'endosurgery-3d-spectar', blurb: 'True-3D visualisation for complex minimally invasive surgery.' },
    ],
    products: [
      { name: 'Rigid Laparoscopes (10 mm 30\u00b0)', link: '/products/rigid-endoscopes/laparoscopes', blurb: 'High-definition laparoscopic optics.' },
      { name: 'XION 3D Endoscope Attachments', link: '/products/rigid-endoscopes/xion-3d-endoscope', blurb: 'Stereoscopic 3D for depth-critical dissection.' },
      { name: 'MATRIX P Spectar Camera Processor', link: '/products/camera-processors-for-surgery/matrix-p-spectar', blurb: 'Reliable surgical camera processor for HD/4K imaging.' },
      { name: 'Spectar 4K Camera Head', link: '/products/camera-heads/spectar-camera-head-4k-uhd', blurb: 'Ultra-high-definition surgical camera head.' },
      { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems/matrix-led-duo', blurb: 'High-power dual LED illumination for the OR.' },
      { name: 'Surgical Medical-Grade Monitors', link: '/products/medical-grade-monitors/medical-monitor-32-surgical', blurb: 'OR-grade 2D / 3D surgical displays.' },
    ],
    highlights: [
      { title: 'True Full 3D', desc: 'Stereoscopic depth for safer, faster instrument handling.' },
      { title: '4K UHD', desc: 'Razor-sharp imaging of fine anatomical structures.' },
      { title: 'PIET enhancement', desc: 'Situational modes that lift contrast and tissue detail.' },
      { title: 'OR integration', desc: 'High-power LED illumination and surgical displays.' },
    ],
  },
  {
    slug: 'urology',
    name: 'Urology',
    fullName: 'Urology',
    kicker: '// Endoscopic Urology',
    tagline: 'Modular 4K / 3D visualisation for endoscopic urology',
    heroImage: '/products/transparent/urology-system.png',
    intro: [
      'XION\u2019s modular endoscopy platform extends naturally into urology. The EndoSURGERY Spectar system pairs MATRIX camera processors and high-power LED illumination with 4K and optional 3D imaging for endoscopic urological visualisation.',
      'Flexible video endoscopy and DiVAS documentation complete the workflow. For procedure-specific scopes and configurations, the FMSInc. team will advise on the right XION set-up for your department.',
    ],
    applications: [
      'Endoscopic urological visualisation',
      'Minimally invasive urological procedures',
      '4K / 3D surgical imaging',
      'Flexible endoscopic examinations',
      'Image documentation & archiving',
    ],
    systems: [
      { slug: 'endosurgery-spectar', blurb: 'HD/4K surgical imaging platform adaptable to urological endoscopy.' },
      { slug: 'endosurgery-3d-spectar', blurb: 'Optional true-3D visualisation for depth-critical procedures.' },
    ],
    products: [
      { name: 'MATRIX P Spectar Camera Processor', link: '/products/camera-processors-for-surgery/matrix-p-spectar', blurb: 'Surgical camera processor for HD / 4K imaging.' },
      { name: 'Spectar 4K Camera Head', link: '/products/camera-heads/spectar-camera-head-4k-uhd', blurb: 'Ultra-high-definition imaging for endoscopic urology.' },
      { name: 'Flexible Video Endoscopes', link: '/products/flexible-video-endoscopes/spectar-video-nasopharyngoscopes', blurb: 'High-resolution flexible chip-on-tip endoscopy.' },
      { name: 'MATRIX LED duo Light Source', link: '/products/led-illumination-systems/matrix-led-duo', blurb: 'High-power dual LED illumination.' },
      { name: 'Medical-Grade Monitors', link: '/products/medical-grade-monitors/medical-monitor-27-4k', blurb: '4K surgical displays from 27" upward.' },
      { name: 'DiVAS 2.8 Software', link: '/products/software/divas-2-8', blurb: 'Documentation and archiving of every examination.' },
    ],
    highlights: [
      { title: '4K UHD imaging', desc: 'High-resolution visualisation of fine urological anatomy.' },
      { title: 'Optional True 3D', desc: 'Add stereoscopic depth where it matters most.' },
      { title: 'PIET enhancement', desc: 'Situational image-enhancement modes.' },
      { title: 'Documentation', desc: 'DiVAS capture and archiving, DICOM / HL7 / GDT ready.' },
    ],
  },
];

export const getMedicalField = (slug) => medicalFields.find((f) => f.slug === slug);

export default medicalFields;
